import React, { useState, lazy, Suspense } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { FiCheck, FiCheckCircle, FiDownload, FiFile, FiFileText, FiImage, FiVideo, FiMusic, FiArchive, FiGrid, FiMapPin, FiCornerUpLeft, FiExternalLink, FiZap } from "react-icons/fi";
import { getInitials, getAvatarColor } from "./avatarUtils";
import MessageActions from "./MessageActions";
import ReactionBadges from "./ReactionBadges";
import BotActionCard from "./BotActionCard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { SERVER_URL } from "../../../databucket";
import { setHighlightMessage } from "../../../Slices/chatSlice";

const BOT_ID = 'ai-assistant-bot';

// Route S3 URLs through backend proxy for private bucket access
const getProxyUrl = (url, mode = 'inline') => {
  if (!url || !url.includes('amazonaws.com')) return url;
  return `${SERVER_URL}/download?url=${encodeURIComponent(url)}&mode=${mode}`;
};

const getFileIconComponent = (mimeType) => {
  if (!mimeType) return FiFile;
  if (mimeType.includes("pdf")) return FiFileText;
  if (mimeType.includes("image")) return FiImage;
  if (mimeType.includes("video")) return FiVideo;
  if (mimeType.includes("audio")) return FiMusic;
  if (mimeType.includes("zip") || mimeType.includes("rar") || mimeType.includes("tar")) return FiArchive;
  if (mimeType.includes("sheet") || mimeType.includes("excel") || mimeType.includes("csv")) return FiGrid;
  if (mimeType.includes("word") || mimeType.includes("document")) return FiFileText;
  return FiFile;
};

const getMessageStatus = (message) => {
  const senderId = message.senderId;
  // Count only OTHER users (server includes sender in readBy/deliveredTo at creation)
  const readByOthers = (message.readBy || []).filter((r) => r.userId !== senderId).length;
  const deliveredToOthers = (message.deliveredTo || []).filter((d) => d.userId !== senderId).length;
  // Check arrays first, then status field as fallback (set by socket reducers)
  if (readByOthers >= 1 || message.status === 'read') return 'read';
  if (deliveredToOthers >= 1 || message.status === 'delivered') return 'delivered';
  return 'sent';
};

// Highlight search matches in text
const highlightText = (text, query) => {
  if (!query || !text) return text;
  try {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? <mark key={i} className="chat-search-highlight">{part}</mark>
        : part
    );
  } catch {
    return text;
  }
};

const MessageBubble = ({ message, isOwn, showSender, onImageClick, conversationId, isHighlighted, searchQuery }) => {
  const dispatch = useDispatch();
  const [hovered, setHovered] = useState(false);
  const time = dayjs(message.createdAt).format("h:mm A");
  const msgStatus = isOwn ? getMessageStatus(message) : null;

  // Tap the quoted reply card → scroll to the original message (Teams/Slack/WhatsApp pattern).
  // MessageList auto-clears highlightMessageId after 2 seconds, restoring normal styling.
  const jumpToOriginal = () => {
    const targetId = message.replyTo || message.replyToMessage?.id;
    if (targetId) dispatch(setHighlightMessage(targetId));
  };

  if (message.type === "system") {
    return (
      <div className="chat-bubble-row chat-bubble-row--system">
        <div className="chat-bubble">
          <div className="chat-bubble__content chat-bubble__content--system">
            {message.content}
          </div>
        </div>
      </div>
    );
  }

  const isBotMessage = message.senderId === BOT_ID || message.type === "bot" || message.type === "action-result";
  const variant = isOwn ? "own" : "other";

  return (
    <div
      className={`chat-bubble-row chat-bubble-row--${variant}${isHighlighted ? " chat-bubble--highlighted" : ""}`}
      style={{ position: "relative" }}
    >
      {!isOwn && showSender && (
        isBotMessage ? (
          <div className="chat-avatar chat-avatar--sm chat-avatar--bot">
            <FiZap size={14} />
          </div>
        ) : (
          <div
            className="chat-avatar chat-avatar--sm"
            style={{ background: getAvatarColor(message.senderId) }}
          >
            {getInitials(message.senderName?.split(" ")[0], message.senderName?.split(" ")[1])}
          </div>
        )
      )}
      {!isOwn && !showSender && <div style={{ width: 32 }} />}

      {/* Hover handlers moved to the bubble itself — the row is full-width so hovering anywhere
          on the same horizontal line was triggering the action bar. mouseleave on .chat-bubble
          does NOT fire while the cursor is over MessageActions (which is a positioned descendant),
          so the hover-bridge to reach the action toolbar still works. */}
      <div
        className="chat-bubble"
        style={{ position: "relative" }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Hover action toolbar */}
        {hovered && !message.isDeleted && (
          <MessageActions
            message={message}
            isOwn={isOwn}
            conversationId={conversationId}
          />
        )}

        {!isOwn && showSender && (
          <div className={`chat-bubble__sender ${isBotMessage ? "chat-bubble__sender--bot" : ""}`}>
            {isBotMessage ? "AI Assistant" : message.senderName}
          </div>
        )}

        {/* Forwarded label */}
        {message.forwardedFrom?.senderName && (
          <div className="chat-bubble__forwarded">
            <FiCornerUpLeft size={11} />
            Forwarded from {message.forwardedFrom.senderName}
          </div>
        )}

        {/* Pinned indicator */}
        {message.pinnedBy && (
          <div className="chat-bubble__pinned">
            <FiMapPin size={11} /> Pinned
          </div>
        )}

        <div className={`chat-bubble__content chat-bubble__content--${variant}${message.type === "gif" || message.type === "sticker" ? " chat-bubble__content--media" : ""}`}>
          {message.isDeleted ? (
            <span className="chat-bubble__deleted">This message was deleted</span>
          ) : (
            <>
              {/* Quoted reply — click to scroll to the original message */}
              {message.replyToMessage && (
                <div
                  className="chat-bubble__quoted-reply"
                  onClick={jumpToOriginal}
                  style={{ cursor: "pointer" }}
                  title="Go to original message"
                >
                  <div className="chat-bubble__quoted-reply-name">
                    {message.replyToMessage.senderName}
                  </div>
                  <div className="chat-bubble__quoted-reply-text">
                    {message.replyToMessage.content || (message.replyToMessage.type === "image" ? "📷 Photo" : "📎 File")}
                  </div>
                </div>
              )}

              {/* Embedded action card (approve/reject, status updates, etc.) */}
              {message.embeddedAction?.actionType && (
                <BotActionCard message={message} conversationId={conversationId} />
              )}

              {message.type === "gif" && message.content && (
                <img
                  className="chat-bubble__gif"
                  src={message.content}
                  alt={message.fileName || "GIF"}
                  onClick={() => onImageClick?.(message.content, message.fileName || "GIF")}
                />
              )}
              {message.type === "sticker" && message.content && (
                <div className="chat-bubble__sticker">
                  {message.content}
                </div>
              )}
              {message.type === "image" && message.fileUrl && (
                <img
                  className="chat-bubble__image"
                  src={getProxyUrl(message.fileUrl)}
                  alt={message.fileName || "Image"}
                  onClick={() => onImageClick?.(getProxyUrl(message.fileUrl), message.fileName)}
                />
              )}
              {message.type === "file" && message.fileMimeType?.startsWith("audio/") && message.fileUrl && (
                <div className="chat-bubble__audio">
                  <audio src={getProxyUrl(message.fileUrl)} controls preload="metadata" />
                  <span className="chat-bubble__audio-label">Voice message</span>
                </div>
              )}
              {message.type === "file" && !message.fileMimeType?.startsWith("audio/") && (
                <div className={`chat-bubble__file ${!isOwn ? "chat-bubble__file--other" : ""}`}>
                  <a
                    href={getProxyUrl(message.fileUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "inherit", display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0 }}
                  >
                    {React.createElement(getFileIconComponent(message.fileMimeType), { className: "chat-bubble__file-icon" })}
                    <div className="chat-bubble__file-info">
                      <div className="chat-bubble__file-name">
                        {message.fileName || "File"}
                      </div>
                    </div>
                  </a>
                  <a
                    href={`${SERVER_URL}/download?url=${encodeURIComponent(message.fileUrl)}&name=${encodeURIComponent(message.fileName || 'file')}`}
                    title="Download"
                    style={{ color: "inherit", display: "flex", alignItems: "center" }}
                  >
                    <FiDownload className="chat-bubble__file-download" />
                  </a>
                </div>
              )}
              {message.content && message.type !== "gif" && message.type !== "sticker" && (
                <div className="chat-bubble__markdown">
                  {searchQuery ? (
                    <span>{highlightText(message.content, searchQuery)}</span>
                  ) : (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ href, children }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="chat-bubble__link">
                            {children}
                            <FiExternalLink size={10} style={{ marginLeft: 2, display: "inline" }} />
                          </a>
                        ),
                        p: ({ children }) => <span>{children}</span>,
                        code: ({ inline, children }) =>
                          inline ? (
                            <code className="chat-bubble__inline-code">{children}</code>
                          ) : (
                            <pre className="chat-bubble__code-block"><code>{children}</code></pre>
                          ),
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  )}
                </div>
              )}
            </>
          )}
          <div className="chat-bubble__meta">
            {message.editedAt && !message.isDeleted && (
              <span className="chat-bubble__edited">(edited)</span>
            )}
            <span className="chat-bubble__time">{time}</span>
            {isOwn && msgStatus === 'read' && (
              <span className="chat-bubble__status chat-bubble__status--read" title="Read">
                <FiCheck size={12} /><FiCheck size={12} />
              </span>
            )}
            {isOwn && msgStatus === 'delivered' && (
              <span className="chat-bubble__status chat-bubble__status--delivered" title="Delivered">
                <FiCheck size={12} /><FiCheck size={12} />
              </span>
            )}
            {isOwn && msgStatus === 'sent' && (
              <span className="chat-bubble__status chat-bubble__status--sent" title="Sent">
                <FiCheck size={12} />
              </span>
            )}
          </div>
        </div>

        {/* Reaction badges */}
        <ReactionBadges
          reactions={message.reactions}
          messageId={message.id}
          conversationId={conversationId}
        />
      </div>
    </div>
  );
};

export default React.memo(MessageBubble);
